import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { DefaultViewScope, ViewScopeType } from "@verbleif/lib/src/composables/Views/DefaultViewReference";
import type { InheritedScopedViewItem } from "./types";
import { DefaultViewReference } from "@verbleif/lib/src/composables/Views/DefaultViewReference";

/** The icon of the default view: its marker in the sidebar (a house: the list the app opens on) and its "Set as default" menu item. */
export const DEFAULT_VIEW_MARKER_ICON: IconName = "house";

/**
 * The view the API resolved as the start view on the effective views of a user at a location
 * (`default` on at most one item, with `defaultScope`). The API already applied the fallback, so
 * null only means no enabled view without a required scope exists, or the views are not loaded.
 */
export function effectiveDefaultView(views: readonly InheritedScopedViewItem[]): DefaultViewReference | null {
  const item = views.find(view => view.default === true);
  if (!item) {
    return null;
  }
  return DefaultViewReference.create(item.key, item.defaultScope ?? null);
}

export function viewRequiredScope(view: Pick<InheritedScopedViewItem, "definition">): ViewScopeType | null {
  return view.definition?.requiredScope ?? null;
}

/**
 * The scope types that satisfy a required scope. A task list (department) also takes a user
 * without a department (`task_list@user:41`); a user inside a department rides on the task list scope.
 */
export function scopeTypesForRequiredScope(required: ViewScopeType): readonly ViewScopeType[] {
  return required === "taskList" ? ["taskList", "user"] : [required];
}

/**
 * The reference to store when a view becomes the default. A view with a required scope needs a
 * scope of that type (otherwise the API falls back), so null tells the caller there is nothing
 * valid to store.
 */
export function defaultViewReferenceFor(
  view: Pick<InheritedScopedViewItem, "key" | "definition">,
  scope: DefaultViewScope | null,
): DefaultViewReference | null {
  const required = viewRequiredScope(view);
  if (required !== null && (scope === null || !scopeTypesForRequiredScope(required).includes(scope.type))) {
    return null;
  }
  return DefaultViewReference.create(view.key, scope);
}
